import React, { useState, useCallback, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useContentful } from '../contentful-context';
import { getImageUrl, optimizeImageUrl } from '../utils';
import { Helmet } from 'react-helmet';
import { track } from '@vercel/analytics';
import { ModelFeatures } from './Models';
import './Models.css';
import './ModelView.css';

function PlanCard({ label, src, alt, onClick }) {
  return (
    <button type="button" className="model-plan-card" onClick={onClick}>
      <div className="model-plan-card-image">
        <img src={src} alt={alt} loading="lazy" />
      </div>
      <span className="model-plan-card-label">{label}</span>
    </button>
  );
}

function StorySection({ kicker, title, plans }) {
  if (!plans.length) return null;

  return (
    <section className="model-detail-section">
      <div className="model-detail-section-header">
        <p className="model-detail-section-kicker">{kicker}</p>
        <h2 className="model-detail-section-title">{title}</h2>
      </div>
      <div className="model-plan-grid">
        {plans.map((plan) => (
          <PlanCard key={plan.label} {...plan} />
        ))}
      </div>
    </section>
  );
}

export default function ModelView() {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { name } = useParams();
  const content = useContentful();

  let carouselItems = [];
  let model = null;

  if (content.models && content.models.length) {
    model = content.models.find((item) => item.name.toLowerCase() === name);
    if (model) {
      const mainImageUrl = getImageUrl(model, { width: 1000, quality: 85 });
      const mainUrl = model.mainUrl;
      const schematicImageUrl = model.schematicImage?.fields?.file?.url
        ? optimizeImageUrl(model.schematicImage.fields.file.url, { width: 1000, quality: 85 })
        : null;
      const schematicUrl = model.schematicUrl;

      carouselItems = [{ src: mainImageUrl, url: mainUrl, alt: 'Finished home exterior render' }];
      if (schematicImageUrl) {
        carouselItems.push({ src: schematicImageUrl, url: schematicUrl, alt: 'Construx frame schematic' });
      }
    }
  }

  const prevImage = useCallback(() => {
    setCarouselIndex((prev) => (prev === 0 ? carouselItems.length - 1 : prev - 1));
  }, [carouselItems.length]);

  const nextImage = useCallback(() => {
    setCarouselIndex((prev) => (prev === carouselItems.length - 1 ? 0 : prev + 1));
  }, [carouselItems.length]);

  useEffect(() => {
    if (isHovered || carouselItems.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev === carouselItems.length - 1 ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(timer);
  }, [carouselItems.length, isHovered]);

  if (!content.models.length || !model) {
    return null;
  }

  const carouselSubtitles = ['Finished model', 'Construx frame'];
  const subtitle = carouselSubtitles[carouselIndex] || '';
  const activeSlide = carouselItems[carouselIndex];

  const openImage = (url, imageType) => {
    track('floor_plan_viewed', {
      modelName: name,
      imageType,
    });
    window.open(url, '_blank');
  };

  const openCarouselSlide = () => {
    if (!activeSlide?.url) return;
    track('model_3d_opened', {
      modelName: name,
      viewType: carouselIndex === 0 ? 'finished_home' : 'construx_frame',
    });
    window.open(activeSlide.url, '_blank');
  };

  const { dRender, floorPlan, extendedImage, extendedImageSecond, body, features, type } = model;

  const extendedImageUrl = extendedImage.fields.file.url;
  const extendedImageSecondUrl = extendedImageSecond?.fields?.file?.url;
  const renderImageUrl = optimizeImageUrl(dRender.fields.file.url, { width: 800, quality: 85 });
  const floorPlanImageUrl = optimizeImageUrl(floorPlan.fields.file.url, { width: 800, quality: 85 });
  const secondStoryRenderUrl =
    type === 'Two Story'
      ? optimizeImageUrl(model.dRenderSecondStory.fields.file.url, { width: 800, quality: 85 })
      : null;
  const secondStoryFloorPlanUrl =
    type === 'Two Story'
      ? optimizeImageUrl(model.secondStoryFloorPlan.fields.file.url, { width: 800, quality: 85 })
      : null;

  const firstStoryPlans = [
    {
      label: 'Floor plan',
      src: floorPlanImageUrl,
      alt: `${model.name} first story floor plan`,
      onClick: () => openImage(extendedImageUrl, 'first_story_floor_plan'),
    },
    {
      label: '3D render',
      src: renderImageUrl,
      alt: `${model.name} first story 3D render`,
      onClick: () => openImage(renderImageUrl, 'first_story_render'),
    },
  ];

  const secondStoryPlans = [];
  if (secondStoryFloorPlanUrl) {
    secondStoryPlans.push({
      label: 'Floor plan',
      src: secondStoryFloorPlanUrl,
      alt: `${model.name} second story floor plan`,
      onClick: () => openImage(extendedImageSecondUrl, 'second_story_floor_plan'),
    });
  }
  if (secondStoryRenderUrl) {
    secondStoryPlans.push({
      label: '3D render',
      src: secondStoryRenderUrl,
      alt: `${model.name} second story 3D render`,
      onClick: () => openImage(secondStoryRenderUrl, 'second_story_render'),
    });
  }

  return (
    <div className="model-detail-page font-bai bg-warm-50 min-h-screen">
      <Helmet>
        <title>{model.name} — Construx</title>
        <meta
          name="description"
          content={body ? body.slice(0, 155) : `View the ${model.name} Construx model — floor plans, renders, and specifications.`}
        />
      </Helmet>

      <section className="model-detail-hero">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-14">
          <Link to="/models" className="model-detail-back mb-5">
            ← All models
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h1 className="model-detail-name">{model.name}</h1>
            {type ? <span className="model-card-type">{type}</span> : null}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <div className="model-detail-main">
          <div
            className="model-detail-carousel group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="model-detail-carousel-stage">
              {carouselItems.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevImage}
                    className="model-detail-carousel-nav prev"
                    aria-label="Previous image"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={nextImage}
                    className="model-detail-carousel-nav next"
                    aria-label="Next image"
                  >
                    →
                  </button>
                </>
              )}
              <img
                src={activeSlide.src}
                alt={activeSlide.alt}
                className={activeSlide.url ? 'is-clickable' : undefined}
                onClick={activeSlide.url ? openCarouselSlide : undefined}
              />
            </div>
            <div className="model-detail-carousel-footer">
              <p className="model-detail-carousel-caption">{subtitle}</p>
              {carouselItems.length > 1 && (
                <div className="model-detail-dots">
                  {carouselItems.map((item, idx) => (
                    <button
                      key={item.alt}
                      type="button"
                      className={`model-detail-dot ${carouselIndex === idx ? 'is-active' : ''}`}
                      onClick={() => setCarouselIndex(idx)}
                      aria-label={`Show ${carouselSubtitles[idx]}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="model-detail-sidebar">
            <ModelFeatures features={features[0]} />
            <div className="model-detail-body">
              {body ? <p className="mb-5">{body}</p> : null}
              <Link to="/interest" className="model-detail-cta-link">
                Get in touch about this model
              </Link>
            </div>
          </div>
        </div>

        <StorySection kicker="Layout" title="First story" plans={firstStoryPlans} />
        {secondStoryPlans.length > 0 && (
          <StorySection kicker="Layout" title="Second story" plans={secondStoryPlans} />
        )}
      </section>
    </div>
  );
}
